import React, { useEffect, useState } from 'react'

import { getNodeChildren, getView } from 'utils/dom'

function applyOrder(children: HTMLElement[]) {
  children.forEach((element, index) => {
    element.style.order = String(index)
  })
}

const changeVisibility = (element: HTMLElement, isHidden: boolean) => {
  if (isHidden) {
    element.style.visibility = 'hidden'
    element.style.position = 'absolute'
  } else {
    element.style.visibility = ''
    element.style.position = 'unset'
  }
}

function getVisibleIndex(
  children: HTMLElement[],
  rect: DOMRectReadOnly,
  gap: number,
  maxVisibleIndex = Infinity,
) {
  if (children.length <= 1) {
    return -1
  }

  const more = children[children.length - 1]
  const moreRect = more.getBoundingClientRect()
  const rightWithOverflow = rect.right - moreRect.width - gap
  const lastItemIndex = children.length - 2
  const childrenWithoutMore = children.slice(0, -1)

  let childRight = 0
  let lastVisibleIndex = lastItemIndex
  let lastVisibleIndexWithOverflow = lastItemIndex - 1

  childrenWithoutMore.forEach((element, index) => {
    const childRect = element.getBoundingClientRect()

    childRight += index === 0 ? rect.left + childRect.width : childRect.width + gap

    if (childRight > rect.right && lastVisibleIndex === lastItemIndex) {
      lastVisibleIndex = index - 1
    }

    if (
      childRight > rightWithOverflow &&
      lastVisibleIndexWithOverflow === lastItemIndex - 1
    ) {
      lastVisibleIndexWithOverflow = index - 1
    }
  })

  const visibleIndex = Math.min(
    lastVisibleIndex < lastItemIndex ? lastVisibleIndexWithOverflow : lastVisibleIndex,
    maxVisibleIndex,
  )

  childrenWithoutMore.forEach((element, index) => {
    changeVisibility(element, index > visibleIndex)
  })

  changeVisibility(
    more,
    lastVisibleIndex === lastItemIndex && visibleIndex === lastItemIndex,
  )
  more.style.order = String(visibleIndex)

  return visibleIndex
}

type MoreBarProviderProps = {
  wrapperRef: React.RefObject<HTMLElement | null>
  maxVisibleIndex?: number
}

export const useVisibleElements = ({
  wrapperRef,
  maxVisibleIndex,
}: MoreBarProviderProps) => {
  const [visibleIndex, setVisibleIndex] = useState<number>(-1)

  useEffect(() => {
    const barElement = wrapperRef.current

    if (barElement) {
      const view = getView(barElement)

      if (!view || view.ResizeObserver == null || view.MutationObserver == null) {
        return undefined
      }

      const styles = view.getComputedStyle(barElement)
      const gap = parseFloat(
        (styles.columnGap ?? '0').replace(/px$/, '').replace(/^normal$/, '0'),
      )

      let barElementChildren = getNodeChildren(barElement)
      const resizeObserver = new view.ResizeObserver(entries => {
        setVisibleIndex(
          getVisibleIndex(
            barElementChildren,
            entries[0].contentRect,
            gap,
            maxVisibleIndex,
          ),
        )
      })

      const mutationObserver = new view.MutationObserver(() => {
        barElementChildren = getNodeChildren(barElement)
        applyOrder(barElementChildren)
        setVisibleIndex(
          getVisibleIndex(
            barElementChildren,
            barElement.getBoundingClientRect(),
            gap,
            maxVisibleIndex,
          ),
        )
      })

      applyOrder(barElementChildren)
      resizeObserver.observe(barElement)
      mutationObserver.observe(barElement, {
        subtree: true,
        childList: true,
        characterData: true,
      })

      return () => {
        resizeObserver.disconnect()
        mutationObserver.disconnect()
      }
    }

    return undefined
  }, [wrapperRef, maxVisibleIndex])

  return {
    visibleIndex,
  }
}
